import React, {FC, FormEvent, useEffect, useMemo, useState} from 'react';
import '../souhait.css';

type WishMode = 'pledge' | 'gift' | 'choose';
type CaptureStep = 'url' | 'loading' | 'review';

interface Wish {
  id: string;
  number: string;
  title: string;
  note: string;
  image: string;
  merchant: string;
  price: number;
  pledged: number;
  pledgers: number;
  mode: WishMode;
  shape: string;
  sourceUrl: string;
  edition?: string;
  status?: string;
  custom?: boolean;
}

interface Pledge {
  id: string;
  wishId: string;
  amount: number;
  name: string;
  message: string;
  createdAt: string;
}

interface MetadataDraft {
  url: string;
  title: string;
  merchant: string;
  image: string;
  price: string;
  edition: string;
  note: string;
  mode: WishMode;
}

interface CaptureModeOption {
  mode: WishMode;
  label: string;
  description: string;
}

const WISHES_KEY = 'souhait:wishes:v2';
const PLEDGES_KEY = 'souhait:pledges:v1';
const CAPTAINS_KEY = 'souhait:captains:v1';

const initialWishes: Wish[] = [
  {
    id: 'game-ui-bible',
    number: '01',
    title: 'The Game UI Bible',
    note: 'The signed edition, if it is still available. A book I know I will keep returning to.',
    image: '/images/souhait/design-book.jpg',
    merchant: 'Lost in Cult',
    price: 120,
    pledged: 78,
    pledgers: 4,
    mode: 'pledge',
    shape: 'wish--wide',
    sourceUrl: 'https://www.lostincult.co.uk/gameuibible',
    edition: 'Signed limited edition',
    status: 'Pre-order',
  },
  {
    id: 'cobalt-chair',
    number: '02',
    title: 'A chair worth reading in',
    note: 'Deep blue, generous, and just strange enough. This is the one I keep thinking about.',
    image: '/images/souhait/cobalt-chair.jpg',
    merchant: 'Hem',
    price: 890,
    pledged: 0,
    pledgers: 0,
    mode: 'gift',
    shape: 'wish--portrait',
    sourceUrl: 'https://example.com/cobalt-chair',
  },
  {
    id: 'cabin-weekend',
    number: '03',
    title: 'Two quiet nights away',
    note: 'A small cabin, cold water, long walks, no notifications. Dates are completely flexible.',
    image: '/images/souhait/cabin-escape.jpg',
    merchant: 'Somewhere in the woods',
    price: 340,
    pledged: 215,
    pledgers: 7,
    mode: 'choose',
    shape: 'wish--tall',
    sourceUrl: 'https://example.com/cabin-weekend',
    edition: 'Dates flexible',
    status: 'Dream wish',
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);

const directionSixImage = (image: string) => {
  const replacements: Record<string, string> = {
    '/images/souhait/design-book.jpg':
      '/images/souhait/illustrations/wish-game-ui-book.webp',
    '/images/souhait/cobalt-chair.jpg':
      '/images/souhait/illustrations/wish-reading-chair.webp',
    '/images/souhait/cabin-escape.jpg':
      '/images/souhait/illustrations/wish-cabin-getaway.webp',
    '/images/souhait/hero-collection.jpg':
      '/images/souhait/illustrations/wish-fallback.webp',
  };
  return replacements[image] || image;
};

const useBodyLock = () => {
  useEffect(() => {
    document.body.classList.add('souhait-lock');
    return () => document.body.classList.remove('souhait-lock');
  }, []);
};

function safeRead<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch (error) {
    return fallback;
  }
}

const toTitle = (value: string) =>
  value
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const fallbackMetadata = (url: string): MetadataDraft => {
  const parsed = new URL(url);
  const pathPart = parsed.pathname.split('/').filter(Boolean).pop() || 'A new wish';
  return {
    url,
    title: toTitle(pathPart),
    merchant: parsed.hostname.replace(/^www\./, ''),
    image: '/images/souhait/hero-collection.jpg',
    price: '',
    edition: '',
    note: '',
    mode: 'pledge',
  };
};

const extractMetadata = async (url: string): Promise<MetadataDraft> => {
  if (url.includes('lostincult.co.uk/gameuibible')) {
    return {
      url,
      title: 'The Game UI Bible',
      merchant: 'Lost in Cult',
      image: '/images/souhait/design-book.jpg',
      price: '120',
      edition: 'Signed limited edition',
      note: '',
      mode: 'pledge',
    };
  }

  const fallback = fallbackMetadata(url);
  try {
    const response = await fetch(url, {mode: 'cors'});
    if (!response.ok) return fallback;
    const html = await response.text();
    const documentNode = new DOMParser().parseFromString(html, 'text/html');
    const property = (name: string) => {
      const element = documentNode.querySelector(`meta[property="${name}"]`);
      return element ? element.getAttribute('content') || '' : '';
    };
    const named = (name: string) => {
      const element = documentNode.querySelector(`meta[name="${name}"]`);
      return element ? element.getAttribute('content') || '' : '';
    };
    const title =
      property('og:title') || named('twitter:title') || documentNode.title;
    const image = property('og:image') || named('twitter:image');
    return {
      ...fallback,
      title: title || fallback.title,
      image: image || fallback.image,
    };
  } catch (error) {
    return fallback;
  }
};

const captureModes: CaptureModeOption[] = [
  {
    mode: 'pledge',
    label: 'Pledge together',
    description: 'No money moves until a gift captain coordinates.',
  },
  {
    mode: 'gift',
    label: 'Gift this',
    description: 'One person quietly takes care of it.',
  },
  {
    mode: 'choose',
    label: 'Choose for me',
    description: 'The giver can decide the final version.',
  },
];

const modeLabel = (mode: WishMode) => {
  if (mode === 'gift') return 'Gift this';
  if (mode === 'choose') return 'Choose for me';
  return 'Pledge together';
};

const RouteLink: FC<{
  href: string;
  navigate: (path: string) => void;
  className?: string;
}> = ({href, navigate, className, children}) => (
  <a
    className={className}
    href={href}
    onClick={event => {
      event.preventDefault();
      navigate(href);
    }}
  >
    {children}
  </a>
);

const Brand: FC<{navigate: (path: string) => void; light?: boolean}> = ({
  navigate,
  light = false,
}) => (
  <RouteLink
    className={`wordmark${light ? ' wordmark--light' : ''}`}
    href="/"
    navigate={navigate}
  >
    <img
      alt="souha.it"
      src="/images/souhait/brand/logo-lockup-320.png"
    />
  </RouteLink>
);

const Topbar: FC<{
  navigate: (path: string) => void;
  variant: 'marketing' | 'public' | 'owner';
  onShare?: () => void;
  copied?: boolean;
}> = ({navigate, variant, onShare, copied}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="topbar">
      <Brand navigate={navigate} />
      <nav className={menuOpen ? 'topbar__nav is-open' : 'topbar__nav'}>
        {variant === 'marketing' && (
          <>
            <RouteLink href="/julien" navigate={navigate}>
              See a real list
            </RouteLink>
            <a href="#how-it-works">How it works</a>
          </>
        )}
        {variant === 'public' && (
          <>
            <a href="#wishes">Julien&apos;s wishes</a>
            <button className="text-button" type="button" onClick={onShare}>
              {copied ? 'Link copied ✓' : 'Share this list ↗'}
            </button>
          </>
        )}
        {variant === 'owner' && (
          <>
            <a href="#my-wishes">My wishes</a>
            <a href="#gift-room">Gift room</a>
          </>
        )}
      </nav>
      <RouteLink
        className="add-button topbar__route"
        href={variant === 'owner' ? '/julien' : '/me'}
        navigate={navigate}
      >
        {variant === 'owner' ? 'View my list ↗' : 'Start a list ＋'}
      </RouteLink>
      <button
        className="menu-button"
        type="button"
        aria-label="Toggle navigation"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(value => !value)}
      >
        <span />
        <span />
      </button>
    </header>
  );
};

const MarketingPage: FC<{navigate: (path: string) => void}> = ({navigate}) => (
  <main className="souhait">
    <Topbar navigate={navigate} variant="marketing" />
    <section className="hero" id="top">
      <div className="hero__image" aria-hidden="true" />
      <div className="hero__shade" aria-hidden="true" />
      <div className="hero__orbit hero__orbit--one" aria-hidden="true">
        things
      </div>
      <div className="hero__orbit hero__orbit--two" aria-hidden="true">
        stories
      </div>
      <div className="hero__content">
        <p className="eyebrow hero__eyebrow">A wishlist from everywhere</p>
        <h1>
          Keep the things
          <br />
          <em>you mean.</em>
        </h1>
        <p className="hero__intro">
          Save anything you would genuinely love. Tell people why. Let friends
          quietly make it happen.
        </p>
        <div className="hero__actions">
          <RouteLink className="primary-button" href="/me" navigate={navigate}>
            Start your collection <span>↗</span>
          </RouteLink>
          <a className="circle-link" href="#how-it-works" aria-label="How it works">
            <span>↓</span>
          </a>
        </div>
      </div>
      <div className="hero__caption">
        <span>Wish from anywhere</span>
        <span>Give without guessing</span>
      </div>
    </section>

    <section className="manifesto" aria-label="What souha.it is">
      <div className="manifesto__mark" aria-hidden="true">*</div>
      <p>
        Not a shopping list.
        <br />
        <strong>A portrait in objects.</strong>
      </p>
      <p className="manifesto__small">
        The internet is full of things worth wanting. souha.it keeps their
        story intact.
      </p>
    </section>

    <section className="how" id="how-it-works">
      <div className="how__sticky">
        <p className="eyebrow">One quiet loop</p>
        <h2>Wish from<br />anywhere.</h2>
        <RouteLink className="outline-button" href="/me" navigate={navigate}>
          Try adding one ↗
        </RouteLink>
      </div>
      <ol className="how__steps">
        <li>
          <span>01</span>
          <div>
            <h3>Paste a link.</h3>
            <p>
              A book, an old chair, a train ticket, something made by one
              person—if it has a URL, it belongs here.
            </p>
          </div>
        </li>
        <li>
          <span>02</span>
          <div>
            <h3>Say why this one.</h3>
            <p>
              One honest sentence turns a product into a wish someone can
              understand.
            </p>
          </div>
        </li>
        <li>
          <span>03</span>
          <div>
            <h3>Let people help.</h3>
            <p>
              Friends can reserve it or make no-charge pledges together before
              one gift captain completes the purchase.
            </p>
          </div>
        </li>
      </ol>
    </section>
    <SiteFooter navigate={navigate} />
  </main>
);

const SiteFooter: FC<{navigate: (path: string) => void}> = ({navigate}) => (
  <footer className="footer">
    <p className="footer__line">
      Something wonderful
      <br />
      is worth <em>wishing for.</em>
    </p>
    <RouteLink className="footer__cta" href="/me" navigate={navigate}>
      <span>Start a list</span>
      <b>↗</b>
    </RouteLink>
    <div className="footer__bottom">
      <Brand navigate={navigate} light />
      <p>Made for thoughtful giving.</p>
      <p>Paris · 2026</p>
    </div>
  </footer>
);

const WishGrid: FC<{
  wishes: Wish[];
  pledges: Pledge[];
  onSelect: (wish: Wish) => void;
}> = ({wishes, pledges, onSelect}) => (
  <div className="wish-grid">
    {wishes.map((wish, index) => {
      const pledged =
        wish.pledged +
        pledges
          .filter(pledge => pledge.wishId === wish.id)
          .reduce((total, pledge) => total + pledge.amount, 0);
      const percentage = Math.min(100, Math.round((pledged / wish.price) * 100));
      return (
        <article
          className={`wish ${wish.shape}`}
          key={wish.id}
          style={{'--wish-index': index} as React.CSSProperties}
        >
          <button
            className="wish__image-button"
            type="button"
            onClick={() => onSelect(wish)}
            aria-label={`Open ${wish.title}`}
          >
            <img
              src={directionSixImage(wish.image)}
              alt={wish.title}
              onError={event => {
                event.currentTarget.src =
                  '/images/souhait/illustrations/wish-fallback.webp';
              }}
            />
            <span className="wish__number">{wish.number}</span>
            {wish.status && <span className="wish__status">{wish.status}</span>}
            <span className="wish__reveal">View the wish ↗</span>
          </button>
          <div className="wish__body">
            <div className="wish__meta">
              <span>{wish.merchant}</span>
              <span>{formatCurrency(wish.price)}</span>
            </div>
            <h3>{wish.title}</h3>
            <p>{wish.note}</p>
            <div className="wish__footer">
              <button type="button" onClick={() => onSelect(wish)}>
                {modeLabel(wish.mode)}
              </button>
              {pledged > 0 && (
                <div className="mini-progress" aria-label={`${percentage}% pledged`}>
                  <span style={{width: `${percentage}%`}} />
                </div>
              )}
            </div>
          </div>
        </article>
      );
    })}
  </div>
);

const PublicListPage: FC<{
  navigate: (path: string) => void;
  wishes: Wish[];
  pledges: Pledge[];
  captains: string[];
  setPledges: React.Dispatch<React.SetStateAction<Pledge[]>>;
  setCaptains: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({navigate, wishes, pledges, captains, setPledges, setCaptains}) => {
  const [selected, setSelected] = useState<Wish | null>(null);
  const [copied, setCopied] = useState(false);
  const copyList = async () => {
    setCopied(true);
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${window.location.origin}/julien`);
    }
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <main className="souhait public-page">
      <Topbar
        navigate={navigate}
        variant="public"
        onShare={copyList}
        copied={copied}
      />
      <section className="public-hero">
        <div className="public-hero__portrait" aria-hidden="true" />
        <div className="public-hero__copy">
          <p className="eyebrow">Julien&apos;s collection · 2026</p>
          <h1>
            Things I would
            <br />
            <em>love to live with.</em>
          </h1>
          <p>
            Books, objects and small escapes. Nothing urgent; everything
            considered.
          </p>
          <a href="#wishes">Explore the wishes ↓</a>
        </div>
        <div className="public-hero__note">
          <span>03 + {wishes.filter(wish => wish.custom).length} wishes</span>
          <span>Keep it a surprise</span>
        </div>
      </section>
      <section className="collection public-collection" id="wishes">
        <div className="collection__heading">
          <div>
            <p className="eyebrow">For people who know me</p>
            <h2>Why these<br />particular things.</h2>
          </div>
          <p>
            Open a wish to reserve it privately, make a no-charge pledge, or
            volunteer to coordinate the group gift.
          </p>
        </div>
        <WishGrid wishes={wishes} pledges={pledges} onSelect={setSelected} />
      </section>
      <footer className="public-footer">
        <p>End of Julien&apos;s list.</p>
        <RouteLink href="/" navigate={navigate}>
          Make a collection of your own ↗
        </RouteLink>
      </footer>
      {selected && (
        <WishDetail
          wish={selected}
          pledges={pledges}
          captains={captains}
          onClose={() => setSelected(null)}
          onPledge={pledge => setPledges(current => [...current, pledge])}
          onCaptain={() =>
            setCaptains(current =>
              current.includes(selected.id) ? current : [...current, selected.id]
            )
          }
        />
      )}
    </main>
  );
};

const OwnerPage: FC<{
  navigate: (path: string) => void;
  wishes: Wish[];
  pledges: Pledge[];
  captains: string[];
  onAddWish: (wish: Wish) => void;
}> = ({navigate, wishes, pledges, captains, onAddWish}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  return (
    <main className="souhait owner-page">
      <Topbar navigate={navigate} variant="owner" />
      <section className="owner-intro">
        <div>
          <p className="eyebrow">Your private workspace</p>
          <h1>Good morning,<br /><em>Julien.</em></h1>
        </div>
        <button className="owner-add" type="button" onClick={() => setAddOpen(true)}>
          <span>＋</span>
          <b>Add something<br />you would love</b>
        </button>
      </section>

      <section className="owner-workspace" id="my-wishes">
        <aside className="owner-rail">
          <p className="eyebrow">Your collections</p>
          <button className="is-active" type="button">
            Everything <span>{wishes.length}</span>
          </button>
          <button type="button">Books to keep <span>1</span></button>
          <button type="button">Someday objects <span>1</span></button>
          <button type="button">Small escapes <span>1</span></button>
          <button className="owner-rail__new" type="button">＋ New collection</button>
        </aside>
        <div className="owner-list">
          <div className="owner-list__top">
            <div>
              <p className="eyebrow">Everything</p>
              <h2>{wishes.length} wishes, kept together.</h2>
            </div>
            <button type="button" onClick={() => setAddOpen(true)}>
              Add a wish ＋
            </button>
          </div>
          {wishes.map((wish, index) => (
            <article className="owner-wish-row" key={wish.id}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <img
                src={directionSixImage(wish.image)}
                alt={wish.title}
                onError={event => {
                  event.currentTarget.src =
                    '/images/souhait/illustrations/wish-fallback.webp';
                }}
              />
              <div>
                <h3>{wish.title}</h3>
                <p>{wish.merchant} · {modeLabel(wish.mode)}</p>
              </div>
              <strong>{formatCurrency(wish.price)}</strong>
              <button type="button" aria-label={`Edit ${wish.title}`}>•••</button>
            </article>
          ))}
        </div>
      </section>

      <section className="gift-room" id="gift-room">
        <div>
          <p className="eyebrow">Surprise-safe by design</p>
          <h2>Your gift room</h2>
          <p>
            Friends coordinate here without revealing names, amounts, or
            messages to you.
          </p>
        </div>
        <div className="gift-room__summary">
          <span>Active group gifts</span>
          <strong>{new Set(pledges.map(pledge => pledge.wishId)).size}</strong>
          <small>{captains.length} gift captain{captains.length === 1 ? '' : 's'} in place</small>
        </div>
        <div className="gift-room__privacy">
          <span aria-hidden="true">✦</span>
          <p>
            We will only tell you when a wish is fully covered. Until then,
            the surprise belongs to them.
          </p>
        </div>
      </section>

      {addOpen && (
        <CaptureDrawer
          onClose={() => setAddOpen(false)}
          onSave={wish => {
            onAddWish(wish);
            setAddOpen(false);
            setSavedMessage('Wish saved. It is already on your public list.');
          }}
          count={wishes.length}
        />
      )}
      {savedMessage && (
        <Toast message={savedMessage} onDismiss={() => setSavedMessage('')} />
      )}
    </main>
  );
};

const CaptureDrawer: FC<{
  onClose: () => void;
  onSave: (wish: Wish) => void;
  count: number;
}> = ({onClose, onSave, count}) => {
  useBodyLock();
  const [step, setStep] = useState<CaptureStep>('url');
  const [url, setUrl] = useState('');
  const [draft, setDraft] = useState<MetadataDraft | null>(null);
  const [extractionNote, setExtractionNote] = useState('');

  const findDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStep('loading');
    const result = await extractMetadata(url);
    setDraft(result);
    setExtractionNote(
      result.image.startsWith('/images/')
        ? 'We found the essentials. Check the details before saving.'
        : 'Details found from the page. Everything remains editable.'
    );
    setStep('review');
  };

  const update = (field: keyof MetadataDraft, value: string) => {
    setDraft(current => (current ? {...current, [field]: value} : current));
  };

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft) return;
    const wish: Wish = {
      id: `wish-${Date.now()}`,
      number: String(count + 1).padStart(2, '0'),
      title: draft.title,
      note: draft.note,
      image: draft.image || '/images/souhait/hero-collection.jpg',
      merchant: draft.merchant,
      price: Number(draft.price) || 0,
      pledged: 0,
      pledgers: 0,
      mode: draft.mode,
      shape: count % 2 === 0 ? 'wish--wide' : 'wish--portrait',
      sourceUrl: draft.url,
      edition: draft.edition || undefined,
      status: 'New',
      custom: true,
    };
    onSave(wish);
  };

  return (
    <div className="drawer-shell" role="presentation">
      <button
        className="drawer-shell__backdrop"
        type="button"
        aria-label="Close add wish panel"
        onClick={onClose}
      />
      <aside className="wish-drawer capture-drawer" aria-label="Add a wish">
        <div className="drawer__top">
          <p className="eyebrow">
            {step === 'review' ? 'Make it yours' : 'A new wish'}
          </p>
          <button className="close-button" type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        {step === 'url' && (
          <>
            <h2>What found you?</h2>
            <p className="drawer__intro">
              Paste any product, experience, or independent shop link.
            </p>
            <form onSubmit={findDetails}>
              <label htmlFor="wish-url">Paste a link</label>
              <div className="url-input">
                <span aria-hidden="true">↗</span>
                <input
                  id="wish-url"
                  type="url"
                  value={url}
                  onChange={event => setUrl(event.target.value)}
                  placeholder="https://somewhere.com/the-thing"
                  required
                  autoFocus
                />
              </div>
              <p className="capture-hint">
                Try the Lost in Cult URL—the prototype recognises it exactly.
                Other links receive a graceful editable preview.
              </p>
              <button className="primary-button drawer__submit" type="submit">
                Find the details <span>↗</span>
              </button>
            </form>
          </>
        )}

        {step === 'loading' && (
          <div className="capture-loading" role="status">
            <span />
            <h2>Looking at<br />the page…</h2>
            <p>Finding its title, image, maker, and current price.</p>
          </div>
        )}

        {step === 'review' && draft && (
          <>
            <div className="capture-preview">
              <img
                src={directionSixImage(draft.image)}
                alt={draft.title}
                onError={event => {
                  event.currentTarget.src =
                    '/images/souhait/illustrations/wish-fallback.webp';
                }}
              />
              <span>{extractionNote}</span>
            </div>
            <form onSubmit={save}>
              <div className="field-pair">
                <label>
                  Title
                  <input
                    value={draft.title}
                    onChange={event => update('title', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Price in EUR
                  <input
                    type="number"
                    min="0"
                    value={draft.price}
                    onChange={event => update('price', event.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="field-pair">
                <label>
                  Maker or shop
                  <input
                    value={draft.merchant}
                    onChange={event => update('merchant', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Edition / size
                  <input
                    value={draft.edition}
                    onChange={event => update('edition', event.target.value)}
                    placeholder="Optional"
                  />
                </label>
              </div>
              <label>
                Image URL
                <input
                  type="text"
                  value={draft.image}
                  onChange={event => update('image', event.target.value)}
                  required
                />
              </label>
              <label>
                Why this one?
                <textarea
                  value={draft.note}
                  onChange={event => update('note', event.target.value)}
                  placeholder="The signed edition, if it is still available…"
                  rows={3}
                  required
                />
              </label>
              <fieldset>
                <legend>How can friends help?</legend>
                {captureModes.map(option => (
                  <label className="mode-option" key={option.mode}>
                    <input
                      type="radio"
                      name="mode"
                      checked={draft.mode === option.mode}
                      onChange={() =>
                        setDraft(current =>
                          current ? {...current, mode: option.mode} : current
                        )
                      }
                    />
                    <span>
                      <b>{option.label}</b>
                      <small>{option.description}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              <button className="primary-button drawer__submit" type="submit">
                Save this wish <span>↗</span>
              </button>
            </form>
          </>
        )}
      </aside>
    </div>
  );
};

const WishDetail: FC<{
  wish: Wish;
  pledges: Pledge[];
  captains: string[];
  onClose: () => void;
  onPledge: (pledge: Pledge) => void;
  onCaptain: () => void;
}> = ({wish, pledges, captains, onClose, onPledge, onCaptain}) => {
  useBodyLock();
  const [amount, setAmount] = useState(25);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const wishPledges = pledges.filter(pledge => pledge.wishId === wish.id);
  const pledged =
    wish.pledged + wishPledges.reduce((total, pledge) => total + pledge.amount, 0);
  const hasCaptain = captains.includes(wish.id);
  const pledge = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onPledge({
      id: `pledge-${Date.now()}`,
      wishId: wish.id,
      amount,
      name,
      message,
      createdAt: new Date().toISOString(),
    });
    setSavedMessage(
      `${formatCurrency(amount)} pledged. Nothing was charged; the gift captain will coordinate next.`
    );
  };

  return (
    <div className="detail-shell" role="dialog" aria-modal="true">
      <button
        className="detail-shell__backdrop"
        type="button"
        aria-label="Close wish"
        onClick={onClose}
      />
      <article className="wish-detail">
        <button className="close-button wish-detail__close" type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="wish-detail__visual">
          <img
            src={directionSixImage(wish.image)}
            alt={wish.title}
            onError={event => {
              event.currentTarget.src =
                '/images/souhait/illustrations/wish-fallback.webp';
            }}
          />
          <span>{wish.number}</span>
        </div>
        <div className="wish-detail__content">
          <p className="eyebrow">{wish.merchant} · {wish.status || 'Available'}</p>
          <h2>{wish.title}</h2>
          <blockquote>“{wish.note}”</blockquote>
          {wish.edition && <p className="edition-note">{wish.edition}</p>}
          <div className="detail-price">
            <span>{formatCurrency(wish.price)}</span>
            <small>Last seen today</small>
          </div>

          {wish.mode !== 'gift' ? (
            <div className="contribution">
              <div className="pledge-notice">
                <b>No payment today.</b>
                <span>This is a promise to join the gift, not a charge.</span>
              </div>
              <div className="contribution__summary">
                <p><strong>{formatCurrency(Math.min(wish.price, pledged))}</strong> pledged</p>
                <p>
                  {Math.max(0, wish.price - pledged) === 0
                    ? 'The group is ready'
                    : `${formatCurrency(wish.price - pledged)} to go`}
                </p>
              </div>
              <div className="progress">
                <span
                  style={{
                    width: `${Math.min(100, Math.round((pledged / wish.price) * 100))}%`,
                  }}
                />
              </div>
              <p className="contributors">
                {wish.pledgers + wishPledges.length} people have made a promise.
              </p>
              {!savedMessage && (
                <form className="pledge-form" onSubmit={pledge}>
                  <div className="amounts">
                    {[15, 25, 50].map(value => (
                      <button
                        className={amount === value ? 'is-active' : ''}
                        type="button"
                        key={value}
                        onClick={() => setAmount(value)}
                      >
                        {formatCurrency(value)}
                      </button>
                    ))}
                  </div>
                  <div className="pledge-fields">
                    <input
                      aria-label="Your first name"
                      placeholder="Your first name"
                      value={name}
                      onChange={event => setName(event.target.value)}
                      required
                    />
                    <input
                      aria-label="A private note"
                      placeholder="A private note (optional)"
                      value={message}
                      onChange={event => setMessage(event.target.value)}
                    />
                  </div>
                  <button className="primary-button contribution__button" type="submit">
                    Pledge {formatCurrency(amount)}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div className="reservation">
              <p>
                Take care of this gift privately. Julien will only see that it
                is covered—not who reserved it.
              </p>
              <button
                className="primary-button contribution__button"
                type="button"
                onClick={() =>
                  setSavedMessage(
                    'It is yours to give. Everyone else will see that it is covered.'
                  )
                }
              >
                I&apos;ll take care of this
              </button>
            </div>
          )}
          {savedMessage && (
            <p className="success-message" role="status">
              <span>✦</span> {savedMessage}
            </p>
          )}
          {wish.mode !== 'gift' && (
            <div className="captain-callout">
              <div>
                <b>{hasCaptain ? 'A gift captain is coordinating.' : 'Could you be the gift captain?'}</b>
                <p>
                  The captain checks availability, confirms the group, and
                  arranges the final purchase outside souha.it.
                </p>
              </div>
              {!hasCaptain && (
                <button
                  type="button"
                  onClick={() => {
                    onCaptain();
                    setSavedMessage('You are the gift captain. The group now has a clear next step.');
                  }}
                >
                  I&apos;ll coordinate ↗
                </button>
              )}
            </div>
          )}
          <a className="merchant-link" href={wish.sourceUrl} target="_blank" rel="noopener noreferrer">
            See it at {wish.merchant} <span>↗</span>
          </a>
        </div>
      </article>
    </div>
  );
};

const Toast: FC<{message: string; onDismiss: () => void}> = ({
  message,
  onDismiss,
}) => (
  <div className="toast" role="status">
    <span>✦</span> {message}
    <button type="button" aria-label="Dismiss" onClick={onDismiss}>×</button>
  </div>
);

const Souhait: FC = () => {
  const [route, setRoute] = useState(window.location.pathname);
  const [wishes, setWishes] = useState<Wish[]>(() =>
    safeRead(WISHES_KEY, initialWishes)
  );
  const [pledges, setPledges] = useState<Pledge[]>(() =>
    safeRead(PLEDGES_KEY, [])
  );
  const [captains, setCaptains] = useState<string[]>(() =>
    safeRead(CAPTAINS_KEY, [])
  );

  useEffect(() => {
    const onPopState = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(WISHES_KEY, JSON.stringify(wishes));
  }, [wishes]);

  useEffect(() => {
    window.localStorage.setItem(PLEDGES_KEY, JSON.stringify(pledges));
  }, [pledges]);

  useEffect(() => {
    window.localStorage.setItem(CAPTAINS_KEY, JSON.stringify(captains));
  }, [captains]);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setRoute(path);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const normalizedRoute = useMemo(
    () => (route === '/julien' || route === '/me' ? route : '/'),
    [route]
  );

  if (normalizedRoute === '/julien') {
    return (
      <PublicListPage
        navigate={navigate}
        wishes={wishes}
        pledges={pledges}
        captains={captains}
        setPledges={setPledges}
        setCaptains={setCaptains}
      />
    );
  }

  if (normalizedRoute === '/me') {
    return (
      <OwnerPage
        navigate={navigate}
        wishes={wishes}
        pledges={pledges}
        captains={captains}
        onAddWish={wish => setWishes(current => [...current, wish])}
      />
    );
  }

  return <MarketingPage navigate={navigate} />;
};

export default Souhait;
